import { CloudRain, Thermometer, Wind, CloudHail, CloudFog, Cloud, Gauge, Waves, TriangleAlert, Snowflake, MountainSnow } from "lucide-react";

export const PARAMS = [
  { id: "rain", label: "Yağış", unit: "mm/sa", key: "precipitation", icon: CloudRain, digits: 1,
    stops: [[0, "#0f172a", 0], [0.1, "#0284c7", 0.4], [1, "#2563eb", 0.6], [3, "#7c3aed", 0.72], [8, "#d97706", 0.8], [20, "#dc2626", 0.85]] },
  { id: "temperature", label: "Sıcaklık", unit: "°C", key: "temperature_2m", icon: Thermometer, digits: 0,
    stops: [[-20, "#1e1b4b", 0.7], [-5, "#3b82f6", 0.7], [5, "#06b6d4", 0.68], [15, "#22c55e", 0.65], [25, "#eab308", 0.68], [35, "#ef4444", 0.75], [45, "#7f1d1d", 0.8]] },
  { id: "wind", label: "Rüzgar", unit: "km/sa", key: "wind_speed_10m", dirKey: "wind_direction_10m", icon: Wind, digits: 0,
    stops: [[0, "#0f172a", 0.3], [10, "#38bdf8", 0.5], [25, "#34d399", 0.6], [40, "#facc15", 0.7], [60, "#f97316", 0.75], [90, "#e11d48", 0.8]] },
  { id: "hail", label: "Dolu Riski", unit: "%", key: "hail", icon: CloudHail, digits: 0,
    stops: [[0, "#0f172a", 0], [10, "#818cf8", 0.35], [30, "#a855f7", 0.55], [60, "#ec4899", 0.72], [85, "#dc2626", 0.85]] },
  { id: "snow", label: "Kar Yağışı", unit: "cm/sa", key: "snowfall", icon: Snowflake, digits: 1,
    stops: [[0, "#0f172a", 0], [0.1, "#e0f2fe", 0.35], [0.5, "#bae6fd", 0.55], [1, "#7dd3fc", 0.65], [3, "#a78bfa", 0.75], [6, "#f0abfc", 0.85]] },
  { id: "snowdepth", label: "Kar Kalınlığı", unit: "cm", key: "snow_depth", icon: MountainSnow, digits: 0, transform: (v) => v * 100,
    stops: [[0, "#0f172a", 0], [1, "#e2e8f0", 0.4], [10, "#bae6fd", 0.6], [30, "#60a5fa", 0.7], [100, "#a78bfa", 0.8], [200, "#f0abfc", 0.85]] },
  { id: "fog", label: "Sis / Görüş", unit: "km", key: "visibility", icon: CloudFog, digits: 1, transform: (v) => v / 1000,
    stops: [[0, "#f8fafc", 0.85], [1, "#cbd5e1", 0.7], [5, "#94a3b8", 0.4], [10, "#64748b", 0.15], [20, "#0f172a", 0]] },
  { id: "cloud", label: "Bulut", unit: "%", key: "cloud_cover", icon: Cloud, digits: 0,
    stops: [[0, "#0f172a", 0], [30, "#475569", 0.3], [60, "#94a3b8", 0.5], [100, "#e2e8f0", 0.7]] },
  { id: "pressure", label: "Basınç", unit: "hPa", key: "pressure_msl", icon: Gauge, digits: 0,
    stops: [[985, "#312e81", 0.7], [1000, "#4338ca", 0.6], [1013, "#6366f1", 0.5], [1025, "#818cf8", 0.6], [1040, "#c7d2fe", 0.7]] },
  { id: "marine", label: "Dalga", unit: "m", key: "wave_height", dirKey: "wave_direction", icon: Waves, digits: 1,
    stops: [[0, "#0c4a6e", 0.5], [0.5, "#0284c7", 0.6], [1, "#38bdf8", 0.65], [2, "#06b6d4", 0.7], [3, "#f59e0b", 0.75], [5, "#ef4444", 0.8]] },
  { id: "road", label: "Yol Riski", unit: "/100", key: "road", icon: TriangleAlert, digits: 0,
    stops: [[0, "#22c55e", 0.3], [30, "#eab308", 0.5], [60, "#f97316", 0.65], [85, "#ef4444", 0.8]] },
];

const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));

export function colorAt(stops, v) {
  if (v <= stops[0][0]) return [...hex(stops[0][1]), stops[0][2]];
  for (let i = 1; i < stops.length; i++) {
    const [v1, c1, a1] = stops[i];
    if (v <= v1) {
      const [v0, c0, a0] = stops[i - 1];
      const f = (v - v0) / (v1 - v0);
      const A = hex(c0), B = hex(c1);
      return [0, 1, 2].map((k) => A[k] + (B[k] - A[k]) * f).concat(a0 + (a1 - a0) * f);
    }
  }
  const last = stops[stops.length - 1];
  return [...hex(last[1]), last[2]];
}

export const valueOf = (param, raw) => (raw == null ? null : param.transform ? param.transform(raw) : raw);

const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
const invMerc = (y) => ((2 * Math.atan(Math.exp(y)) - Math.PI / 2) * 180) / Math.PI;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const smooth = (x) => { const k = clamp(x, 0, 1); return k * k * (3 - 2 * k); };

export function gridBounds(grid) {
  const h = (grid.lats[1] - grid.lats[0]) / 2, w = (grid.lons[1] - grid.lons[0]) / 2;
  return [[grid.lats[0] - h, grid.lons[0] - w], [grid.lats[grid.lats.length - 1] + h, grid.lons[grid.lons.length - 1] + w]];
}

const axis = (f, n) => {
  const i0 = clamp(Math.floor(f), 0, n - 1), i1 = Math.min(n - 1, i0 + 1);
  const fade = smooth((f + 0.5) / 1.5) * smooth((n - 0.5 - f) / 1.5);
  return [i0, i1, clamp(f - i0, 0, 1), fade];
};

// Bilinear interpolation in value space, mercator-corrected rows, soft edge fade
export function buildOverlay(grid, param, t) {
  const rows = grid.lats.length, cols = grid.lons.length, vals = grid.data[param.key][t];
  const [[s, w], [n, e]] = gridBounds(grid);
  const dLat = grid.lats[1] - grid.lats[0], dLon = grid.lons[1] - grid.lons[0];
  const W = cols * 16, H = rows * 16, yN = mercY(n), yS = mercY(s);
  const rowAx = Array.from({ length: H }, (_, py) => axis((invMerc(yN - ((py + 0.5) / H) * (yN - yS)) - grid.lats[0]) / dLat, rows));
  const colAx = Array.from({ length: W }, (_, px) => axis((w + ((px + 0.5) / W) * (e - w) - grid.lons[0]) / dLon, cols));
  const v = vals.map((x) => valueOf(param, x));
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  const img = ctx.createImageData(W, H);
  for (let py = 0; py < H; py++) {
    const [r0, r1, ty, fy] = rowAx[py];
    for (let px = 0; px < W; px++) {
      const [c0, c1, tx, fx] = colAx[px];
      let sum = 0, sw = 0;
      const add = (r, cc, wt) => { const x = v[r * cols + cc]; if (x != null && wt > 0) { sum += x * wt; sw += wt; } };
      add(r0, c0, (1 - ty) * (1 - tx)); add(r0, c1, (1 - ty) * tx); add(r1, c0, ty * (1 - tx)); add(r1, c1, ty * tx);
      if (sw < 0.02) continue;
      const [R, G, B, A] = colorAt(param.stops, sum / sw);
      const o = (py * W + px) * 4;
      img.data[o] = R; img.data[o + 1] = G; img.data[o + 2] = B;
      img.data[o + 3] = Math.round(A * 255 * smooth(sw * 1.4) * fx * fy);
    }
  }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL();
}

export const WEATHER_CODES = {
  0: "Açık", 1: "Az bulutlu", 2: "Parçalı bulutlu", 3: "Kapalı", 45: "Sis", 48: "Kırağılı sis",
  51: "Hafif çisenti", 53: "Çisenti", 55: "Yoğun çisenti", 56: "Donan çisenti", 57: "Yoğun donan çisenti",
  61: "Hafif yağmur", 63: "Yağmur", 65: "Kuvvetli yağmur", 66: "Donan yağmur", 67: "Kuvvetli donan yağmur",
  71: "Hafif kar", 73: "Kar", 75: "Yoğun kar", 77: "Kar taneleri", 80: "Hafif sağanak", 81: "Sağanak",
  82: "Şiddetli sağanak", 85: "Kar sağanağı", 86: "Yoğun kar sağanağı", 95: "Gök gürültülü fırtına",
  96: "Fırtına + dolu", 99: "Fırtına + şiddetli dolu",
};

export const riskLevel = (s) =>
  s == null ? { label: "—", color: "#64748B" }
    : s >= 85 ? { label: "Çok Yüksek", color: "#EF4444" }
    : s >= 60 ? { label: "Yüksek", color: "#F97316" }
    : s >= 30 ? { label: "Orta", color: "#EAB308" }
    : { label: "Düşük", color: "#22C55E" };

export const hailLevel = (h) =>
  h >= 60 ? { label: "Yüksek dolu riski", color: "#EC4899" }
    : h >= 30 ? { label: "Orta dolu riski", color: "#A855F7" }
    : h >= 10 ? { label: "Düşük dolu riski", color: "#818CF8" }
    : { label: "Dolu beklenmiyor", color: "#64748B" };

export const fogLevel = (vis) =>
  vis == null ? { label: "—", color: "#64748B" }
    : vis < 1000 ? { label: "Yoğun sis", color: "#F8FAFC" }
    : vis < 3000 ? { label: "Sisli / puslu", color: "#CBD5E1" }
    : vis < 10000 ? { label: "Hafif pus", color: "#94A3B8" }
    : { label: "Görüş iyi", color: "#22C55E" };

export function roadReasons(h) {
  const r = [];
  if (h.temperature_2m <= 1 && h.precipitation > 0) r.push("Buzlanma riski");
  else if (h.temperature_2m <= 3) r.push("Don / kırağı riski");
  if ([71, 73, 75, 77, 85, 86].includes(h.weather_code)) r.push("Kar yağışı");
  if ([56, 57, 66, 67].includes(h.weather_code)) r.push("Donan yağmur");
  if (h.precipitation >= 2) r.push("Yoğun yağış, kaygan zemin");
  else if (h.precipitation > 0.3) r.push("Islak yol");
  if (h.visibility != null && h.visibility < 3000) r.push("Düşük görüş");
  if (h.wind_speed_10m >= 50) r.push("Kuvvetli rüzgar");
  if (h.hail >= 30) r.push("Dolu tehlikesi");
  return r;
}
